/**
 * RightToolbar.tsx — MapLibre version
 * شريط الأدوات الأيمن — يعمل مع MapLibre بدلاً من Leaflet
 */

import { memo, useState, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
    FiSearch, FiInfo, FiShare2, FiCrosshair,
    FiPlus, FiMinus, FiMaximize2, FiMinimize2, FiLoader, FiSettings, FiMenu,
    FiCopy, FiCheck, FiMail, FiFlag
} from 'react-icons/fi';
import { FaWhatsapp, FaFacebookF, FaTelegramPlane } from 'react-icons/fa';
import { FaXTwitter, FaInstagram, FaTiktok, FaThreads } from 'react-icons/fa6';
import { LuRuler, LuPentagon } from 'react-icons/lu';
import { useShallow } from 'zustand/react/shallow';
import { useWeatherStore } from '../../store/weatherStore';
import { useMapRef } from './MapContext';
import { geocodingService, GeocodingResult } from '../../services/geocodingService';
import { useTranslation } from '../../i18n/LanguageContext';
import { ReportProblemModal } from './ReportProblemModal';
import { MAP_CREDITS } from './MapAttribution';

/** حسابات التواصل الاجتماعي — تظهر في نافذة "عن Sky Radar" (أسلوب Windy/Zoom Earth). */
const SOCIAL_LINKS = [
    { key: 'facebook', Icon: FaFacebookF, color: '#1877F2', href: 'https://www.facebook.com/share/1AZGY3gCaP/' },
    { key: 'x', Icon: FaXTwitter, color: '#000000', href: 'https://x.com/SkyRadarweather' },
    { key: 'instagram', Icon: FaInstagram, color: '#E4405F', href: 'https://www.instagram.com/skyradarweather' },
    { key: 'tiktok', Icon: FaTiktok, color: '#000000', href: 'http://tiktok.com/@skyradarweather' },
    { key: 'threads', Icon: FaThreads, color: '#000000', href: 'https://www.threads.com/@skyradarweather' },
] as const;

/**
 * صنف زرّ الشريط — مُعرَّفٌ مرّة واحدة لا مكرّراً ثماني مرّات في الـ JSX. ثمانِ نسخٍ
 * من سلسلةٍ بهذا الطول تتباعد عند أوّل تعديل يُطبَّق على بعضها ويُنسى في باقيها،
 * فيختلف زرٌّ عن أخيه بلا سبب يراه أحد.
 *
 * الأزرار على الجوّال 40 بكسل داخل اللوحة نفسها لا دوائر منفصلة بـ46: خمس دوائر
 * بفواصل 9 بكسل كانت تشغل 266 بكسل — ثلث شاشة الهاتف — وتُقرأ ككائناتٍ خمسة
 * متناثرة على الخريطة. اللوحة الواحدة 216 بكسل، وهي شيءٌ واحد كما على الديسكتوب.
 */
const TOOL_BTN = 'w-8 h-8 flex items-center justify-center bg-transparent border-none rounded-lg text-inherit light:text-[var(--text-primary)] cursor-pointer text-[15px] transition-all duration-[0.18s] relative hover:bg-white/10 hover:scale-105 light:hover:bg-[var(--row-hover)] light:hover:text-[#667eea] active:scale-[0.96] max-[768px]:w-10 max-[768px]:h-10 max-[768px]:text-[17px]';

/** أزرارٌ لها بديلٌ باللمس على الجوّال (القرص/السحب/زرّ المشاركة في النظام) فتُخفى هناك. */
const TOOL_BTN_DESKTOP = `${TOOL_BTN} max-[768px]:hidden`;

type SearchResult = GeocodingResult;


/* نقطة التوقّف المدمجة — يجب أن تطابق `max-[768px]` في الـ JSX و`max-width: 1180px`
   في 27-responsive-mobile.css و22-layer-sidebar.css و14-cyclone.css. رقمٌ واحد يقرّر
   التخطيط والسلوك معاً: لو افترقا صار الشريط يبدو جوّالاً ويتصرّف كسطح مكتب (أو العكس)،
   وهو عطبٌ صامت لأن كلّاً منهما صحيح على حدة. */
function isCompactViewport(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
}

/**
 * نافذة تُرسَم في <body> لا داخل شريط الأدوات. الشريط يحمل `position:absolute; z-index:800`
 * فيُنشئ سياق تكديس خاصاً، وكل z-index داخله يُقاس بالنسبة إليه — فنافذة بـ 2000 كانت
 * تُقارَن فعلياً بـ 800 وتقع **تحت** الشعار العائم (950) الذي كان يغطّي زر الإغلاق.
 * البوابة تُخرجها من ذلك السياق فيصبح 2000 مقارَناً بالصفحة كما هو مقصود.
 */
function ModalPortal({ children }: { children: ReactNode }) {
    if (typeof document === 'undefined') return null;
    return createPortal(children, document.body);
}

// بلا props — memo يمنع إعادة الرسم مع كل تغيّر إطار زمني في NewWeatherMap.
export const RightToolbar = memo(function RightToolbar() {
    const { t, locale } = useTranslation();
    const mapRef = useMapRef();
    const {
        sidebarOpen,
        layerControlsOpen,
        infoPanelOpen,
        settingsOpen,
        setCurrentLocation,
        requestLocationPreview,
        setSidebarOpen,
        setLayerControlsOpen,
        setInfoPanelOpen,
        setSettingsOpen,
        setSearchOpen,
        setModalOpen,
        measureMode,
        setMeasureMode,
    } = useWeatherStore(useShallow((s) => ({ sidebarOpen: s.sidebarOpen, layerControlsOpen: s.layerControlsOpen, infoPanelOpen: s.infoPanelOpen, settingsOpen: s.settingsOpen, setCurrentLocation: s.setCurrentLocation, requestLocationPreview: s.requestLocationPreview, setSidebarOpen: s.setSidebarOpen, setLayerControlsOpen: s.setLayerControlsOpen, setInfoPanelOpen: s.setInfoPanelOpen, setSettingsOpen: s.setSettingsOpen, setSearchOpen: s.setSearchOpen, setModalOpen: s.setModalOpen, measureMode: s.measureMode, setMeasureMode: s.setMeasureMode })));

    const [showSearch, setShowSearch]       = useState(false);
    // نعكس حالة البحث للمتجر كي يخفي الشعار الذي يغطّيها (سياق تكديس منفصل)
    useEffect(() => { setSearchOpen(showSearch); }, [showSearch, setSearchOpen]);
    const [showInfo, setShowInfo]           = useState(false);
    const [showShare, setShowShare]         = useState(false);
    const [showReport, setShowReport]       = useState(false);
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    // نعكس حالة النوافذ للمتجر كما نفعل مع البحث: قواعد `.modal-open` تُخفي العناصر
    // العائمة (الشعار/الأزرار) خلف النافذة، وعلى الجوّال يُغلق المتجر بقيّة اللوحات.
    useEffect(() => { setModalOpen(showInfo || showShare || showReport); }, [showInfo, showShare, showReport, setModalOpen]);
    const [searchQuery, setSearchQuery]     = useState('');
    const [isFullscreen, setIsFullscreen]   = useState(false);
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [shareUrl, setShareUrl]           = useState('');
    const [copied, setCopied]               = useState(false);

    const closeFloatingPanels = () => {
        if (!isCompactViewport()) return;
        setSidebarOpen(false);
        setLayerControlsOpen(false);
        setInfoPanelOpen(false);
        setSettingsOpen(false);
    };

    const handleSearchToggle = () => {
        const willOpen = !showSearch;
        setShowSearch(willOpen);
        if (willOpen) { setShowInfo(false); setShowShare(false); setShowMoreMenu(false); closeFloatingPanels(); }
    };

    const handleInfoToggle = () => {
        const willOpen = !showInfo;
        setShowInfo(willOpen);
        if (willOpen) { setShowSearch(false); setShowShare(false); setShowMoreMenu(false); closeFloatingPanels(); }
    };

    // القائمة تُغلق عند اختيار وضع، ويُغلق الوضع نفسه إن كان الزرّ يُطفئ ما هو مشتغل —
    // فيصير الزرّ الواحد فتحاً وإغلاقاً كما هو متوقّع من زرّ مُبدِّل.
    const handleMeasurePick = (mode: 'distance' | 'area') => {
        setShowMoreMenu(false);
        setMeasureMode(measureMode === mode ? 'none' : mode);
    };

    const handleMoreToggle = () => {
        if (measureMode !== 'none') { setMeasureMode('none'); setShowMoreMenu(false); return; }
        const willOpen = !showMoreMenu;
        setShowMoreMenu(willOpen);
        if (willOpen) { setShowSearch(false); setShowInfo(false); setShowShare(false); closeFloatingPanels(); }
    };

    useEffect(() => {
        const timeoutId = setTimeout(async () => {
            if (searchQuery.trim().length < 2) { setSearchResults([]); setSearchLoading(false); return; }
            setSearchLoading(true);
            try {
                const results = await geocodingService.searchCities(searchQuery, 8);
                setSearchResults(results);
            } catch { setSearchResults([]); } finally { setSearchLoading(false); }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    useEffect(() => {
        if (!isCompactViewport()) return;
        if (sidebarOpen || layerControlsOpen || infoPanelOpen || settingsOpen) {
            setShowSearch(false); setShowInfo(false); setShowShare(false); setShowMoreMenu(false);
        }
    }, [sidebarOpen, layerControlsOpen, infoPanelOpen, settingsOpen]);

    // ── Map actions ───────────────────────────────────────────────────────────
    const handleZoomIn  = () => { mapRef.current?.zoomIn();  };
    const handleZoomOut = () => { mapRef.current?.zoomOut(); };

    const handleLocate = () => {
        if (!navigator.geolocation) { alert(t('geo.unsupported')); return; }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                setCurrentLocation(latitude, longitude);
                if (isCompactViewport()) {
                    setInfoPanelOpen(false);
                    requestLocationPreview();
                } else {
                    setInfoPanelOpen(true);
                }
                mapRef.current?.flyTo({ center: [longitude, latitude], zoom: 8 });
            },
            () => alert(t('geo.failed'))
        );
    };

    const handleSelectCity = (city: SearchResult) => {
        setShowSearch(false);
        setSearchQuery('');
        setCurrentLocation(city.latitude, city.longitude);
        setInfoPanelOpen(true);
        const currentZoom = mapRef.current?.getZoom() ?? 4;
        mapRef.current?.flyTo({
            center: [city.longitude, city.latitude],
            zoom: Math.max(currentZoom, 8),
            duration: 1200,
        });
    };

    const handleShare = () => {
        const center = mapRef.current?.getCenter();
        const zoom   = mapRef.current?.getZoom() ?? 4;
        const url = center
            ? `${window.location.origin}?lat=${center.lat.toFixed(4)}&lon=${center.lng.toFixed(4)}&z=${zoom.toFixed(1)}`
            : window.location.href;
        setShareUrl(url);
        setCopied(false);
        setShowShare(true);
        setShowSearch(false);
        setShowInfo(false);
        setShowMoreMenu(false);
        closeFloatingPanels();
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* المتصفح منع الوصول للحافظة */ }
    };

    const shareText = t('share.text');
    const shareTargets = [
        { key: 'whatsapp', label: t('share.whatsapp'),  Icon: FaWhatsapp,      color: '#25D366',
          href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}` },
        { key: 'x',        label: 'X',        Icon: FaXTwitter,      color: '#000000',
          href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}` },
        { key: 'telegram', label: t('share.telegram'), Icon: FaTelegramPlane, color: '#229ED9',
          href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}` },
        { key: 'facebook', label: t('share.facebook'),  Icon: FaFacebookF,     color: '#1877F2',
          href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
        { key: 'email',    label: t('share.email'),  Icon: FiMail,          color: '#6b7280',
          href: `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(`${shareText}\n${shareUrl}`)}` },
    ];

    const handleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    return (
        <>
            {/* z-800 مقصود: الصنف z-[1000] السابق كان معطّلاً لأن CSS يهزم أدوات Tailwind، فالقيمة الفعلية كانت 800 دائماً. */}
            {/* يسار **فيزيائي** لا start: أدوات الخريطة (بحث/موقعي) يساراً والطبقات يميناً
                في اللغتين، كـ Zoom Earth — تبديل اللغة يغيّر النصوص لا مواضع الأزرار. */}
            <div className="absolute top-3 left-3 z-[800] flex flex-col gap-0.5 p-1 rounded-xl bg-[var(--bg-panel)] backdrop-blur-[20px] border border-[var(--panel-border)] shadow-[var(--panel-shadow)] animate-fade-in-right max-[768px]:top-auto max-[768px]:bottom-[86px] max-[768px]:left-2 max-[768px]:gap-0.5 max-[768px]:p-1 panel-open:max-[768px]:hidden">
                <button className={TOOL_BTN} onClick={handleSearchToggle} title={t('toolbar.search')} aria-label={t('toolbar.search')}><FiSearch /></button>
                <button className={TOOL_BTN} onClick={handleLocate} title={t('toolbar.myLocation')} aria-label={t('toolbar.myLocation')}><FiCrosshair /></button>
                <div className="h-px bg-[var(--divider)] mx-1.5 my-1 max-[768px]:hidden" />
                <button className={TOOL_BTN_DESKTOP} onClick={handleZoomIn} title={t('toolbar.zoomIn')} aria-label={t('toolbar.zoomIn')}><FiPlus /></button>
                <button className={TOOL_BTN_DESKTOP} onClick={handleZoomOut} title={t('toolbar.zoomOut')} aria-label={t('toolbar.zoomOut')}><FiMinus /></button>
                <button className={TOOL_BTN_DESKTOP} onClick={handleFullscreen} title={t('toolbar.fullscreen')} aria-label={t('toolbar.fullscreen')}>
                    {isFullscreen ? <FiMinimize2 /> : <FiMaximize2 />}
                </button>

                {/* زرّ «المزيد» — يجمع القياس والمشاركة والمعلومات والإعدادات.
                    العمود على الجوّال كان خمسة أزرار (216px، نحو رُبع ارتفاع الشاشة)
                    وهي مساحةُ خريطةٍ تُؤخذ من القارئ دائماً ليصل أحياناً إلى أدواتٍ
                    نادرة. فبقي في العمود ما يُستعمل مع كل نظرة — البحث وتحديد الموقع —
                    ونزل الباقي إلى قائمةٍ خلف نقرة: ثلاثة أزرار (128px) بدل خمسة.

                    والقياس فقد قائمته المستقلّة: كان زرّاً يفتح قائمةً فيها خياران، أي
                    مستوى تعشيشٍ كامل لخيارين اثنين. صارا صفّين في هذه القائمة نفسها.

                    الغلاف relative كي تُعلَّق القائمة بجانب الزرّ على كل المقاسات. */}
                <div className="relative">
                    <button
                        className={`${TOOL_BTN} ${measureMode !== 'none' || showMoreMenu ? 'bg-[rgba(102,126,234,0.22)] text-[#8ea2ff]' : ''}`}
                        onClick={handleMoreToggle}
                        title={measureMode !== 'none' ? t('measure.stop') : t('toolbar.more')}
                        aria-label={t('toolbar.more')}
                        aria-expanded={showMoreMenu}
                        aria-haspopup="menu"
                    >
                        <FiMenu />
                    </button>

                    {showMoreMenu && measureMode === 'none' && (
                        <div role="menu" className="absolute top-0 left-full ml-2 z-[1000] w-[196px] overflow-hidden rounded-xl border border-[var(--panel-border)] bg-[var(--panel-bg)] p-1 text-inherit shadow-[0_12px_40px_rgba(0,0,0,0.5)] backdrop-blur-[24px] animate-fade-in-right [animation-duration:0.2s] light:text-[var(--text-primary)] light:shadow-[var(--panel-shadow)] max-[768px]:top-auto max-[768px]:bottom-0">
                            <button role="menuitem" className="flex w-full items-center gap-2.5 rounded-lg border-none bg-transparent px-2.5 py-2 text-start text-[12.5px] font-bold text-inherit transition-colors duration-150 hover:bg-[rgba(102,126,234,0.14)] light:hover:bg-[var(--row-hover)]" onClick={() => handleMeasurePick('distance')}>
                                <LuRuler className="shrink-0 text-base opacity-80" />
                                {t('measure.distanceTitle')}
                            </button>
                            <button role="menuitem" className="flex w-full items-center gap-2.5 rounded-lg border-none bg-transparent px-2.5 py-2 text-start text-[12.5px] font-bold text-inherit transition-colors duration-150 hover:bg-[rgba(102,126,234,0.14)] light:hover:bg-[var(--row-hover)]" onClick={() => handleMeasurePick('area')}>
                                <LuPentagon className="shrink-0 text-base opacity-80" />
                                {t('measure.areaTitle')}
                            </button>
                            <div className="h-px bg-[var(--divider)] mx-1.5 my-1" />
                            <button role="menuitem" className="flex w-full items-center gap-2.5 rounded-lg border-none bg-transparent px-2.5 py-2 text-start text-[12.5px] font-bold text-inherit transition-colors duration-150 hover:bg-[rgba(102,126,234,0.14)] light:hover:bg-[var(--row-hover)]" onClick={() => { setShowMoreMenu(false); handleShare(); }}>
                                <FiShare2 className="shrink-0 text-base opacity-80" />
                                {t('toolbar.share')}
                            </button>
                            <button role="menuitem" className="flex w-full items-center gap-2.5 rounded-lg border-none bg-transparent px-2.5 py-2 text-start text-[12.5px] font-bold text-inherit transition-colors duration-150 hover:bg-[rgba(102,126,234,0.14)] light:hover:bg-[var(--row-hover)]" onClick={() => { setShowMoreMenu(false); handleInfoToggle(); }}>
                                <FiInfo className="shrink-0 text-base opacity-80" />
                                {t('toolbar.info')}
                            </button>
                            <button role="menuitem" className="flex w-full items-center gap-2.5 rounded-lg border-none bg-transparent px-2.5 py-2 text-start text-[12.5px] font-bold text-inherit transition-colors duration-150 hover:bg-[rgba(102,126,234,0.14)] light:hover:bg-[var(--row-hover)]" onClick={() => { setShowMoreMenu(false); setSettingsOpen(true); }}>
                                <FiSettings className="shrink-0 text-base opacity-80" />
                                {t('common.settings')}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* z-1000 مقصود: z-[99999] السابق كان معطّلاً لأن CSS يهزم أدوات Tailwind. */}
            {showSearch && (
                <div className="absolute top-3 left-[60px] w-[280px] max-w-[calc(100vw-80px)] z-[1000] overflow-hidden rounded-xl bg-[var(--panel-bg)] backdrop-blur-[24px] border border-[var(--panel-border)] text-inherit light:text-[var(--text-primary)] shadow-[0_12px_40px_rgba(0,0,0,0.5)] light:shadow-[var(--panel-shadow)] animate-fade-in-right [animation-duration:0.25s] max-[768px]:top-[62px] max-[768px]:inset-x-2 max-[768px]:w-auto max-[768px]:max-w-none max-[768px]:rounded-[14px]">
                    <div className="flex items-center gap-2 px-[14px] py-3 border-b border-[var(--border-color)] light:border-[var(--panel-border)]">
                        <FiSearch className="text-lg opacity-50 shrink-0 light:text-[var(--text-muted)]" />
                        <input
                            type="text"
                            className="flex-1 bg-transparent light:bg-[var(--stat-bg)] border-none outline-none text-inherit light:text-[var(--text-primary)] [font-family:'Cairo','Tajawal',sans-serif] text-sm py-0.5 px-0 placeholder:text-[rgba(150,150,150,0.6)] light:placeholder:text-[var(--text-muted)]"
                            placeholder={t('search.placeholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                        />
                        <button className="w-8 h-8 flex items-center justify-center bg-white/[0.08] border-none rounded-md text-inherit cursor-pointer text-xs opacity-60 transition-all duration-200 hover:bg-[rgba(255,80,80,0.18)] hover:text-[#ff5050] hover:opacity-100" onClick={() => setShowSearch(false)} aria-label={t('search.close')}>×</button>
                    </div>
                    <div className="max-h-[380px] overflow-y-auto [overscroll-behavior:contain] [-webkit-overflow-scrolling:touch] p-1.5 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/[0.15] [&::-webkit-scrollbar-thumb]:rounded-sm max-[768px]:max-h-[min(50vh,360px)]">
                        {searchLoading ? (
                            <div className="light:text-[var(--text-secondary)]"><FiLoader className="animate-spin-360" /><span>{t('search.loading')}</span></div>
                        ) : searchQuery.trim().length < 2 ? (
                            <div className="text-center px-3 py-[26px] opacity-[0.68] text-[13px] light:text-[var(--text-secondary)]">{t('search.hint')}</div>
                        ) : searchResults.length === 0 ? (
                            <div className="text-center px-3 py-[26px] opacity-[0.68] text-[13px] light:text-[var(--text-secondary)]">{t('search.empty')}</div>
                        ) : (
                            searchResults.map((city, i) => (
                                <button
                                    key={`${city.latitude}-${city.longitude}-${i}`}
                                    className="w-full flex items-center gap-3 min-h-[50px] px-3 py-2.5 mb-0.5 bg-transparent border-none rounded-[10px] text-inherit light:text-[var(--text-primary)] cursor-pointer text-start transition-[background] duration-150 hover:bg-[rgba(102,126,234,0.12)] light:hover:bg-[var(--row-hover)] focus-visible:bg-[rgba(102,126,234,0.16)]"
                                    onClick={() => handleSelectCity(city)}
                                >
                                    <div className="text-sm w-[26px] h-[26px] flex items-center justify-center bg-white/[0.06] rounded-md shrink-0">📍</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-semibold mb-px">{city.name}</div>
                                        {city.admin1 && <div className="light:text-[var(--text-muted)]">{city.admin1}</div>}
                                        <div className="text-[10px] opacity-55 light:text-[var(--text-muted)]">{city.country} ({city.country_code})</div>
                                    </div>
                                    <div className="text-[10px] opacity-40 [direction:ltr] light:text-[var(--text-muted)]">
                                        {city.latitude.toFixed(2)}°, {city.longitude.toFixed(2)}°
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}

            {showInfo && (
                <ModalPortal>
                <div className="about-modal fixed inset-0 bg-black/70 backdrop-blur-[8px] flex items-center justify-center z-[2000] p-5 animate-fade-in max-[768px]:p-0" onClick={() => setShowInfo(false)}>
                    <div className="about-modal-panel w-full max-w-[480px] max-h-[85vh] bg-[var(--panel-bg)] border border-[var(--panel-border)] text-inherit light:text-[var(--text-primary)] rounded-2xl overflow-hidden flex flex-col animate-fade-in-up max-[768px]:h-[100dvh] max-[768px]:max-h-none max-[768px]:max-w-none max-[768px]:rounded-none max-[768px]:border-0" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center px-[22px] py-[18px] border-b border-[var(--divider)]">
                            <h2 className="m-0 text-lg font-extrabold">{t('about.title')}</h2>
                            <button className="light:text-[var(--text-secondary)]" onClick={() => setShowInfo(false)}>×</button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-[22px] py-5">
                            <div className="mb-5">
                                <h3 className="text-sm font-bold m-0 mb-2 text-[var(--color-primary)]">{t('about.whatTitle')}</h3>
                                <p className="m-0 text-[13px] leading-[1.7] opacity-85">{t('about.whatBody')}</p>
                            </div>
                            <div className="mb-5">
                                <h3 className="text-sm font-bold m-0 mb-2 text-[var(--color-primary)]">{t('about.sourcesTitle')}</h3>
                                <ul className="m-0 p-0 list-none [&>li]:flex [&>li]:items-baseline [&>li]:justify-between [&>li]:gap-3 [&>li]:border-b [&>li]:border-white/[0.06] [&>li]:py-1.5 [&>li]:text-[12px] [&>li]:leading-[1.45] [&>li]:last:border-b-0">
                                    {MAP_CREDITS.map((source) => (
                                        <li key={source.name}>
                                            <span className="opacity-65">{t(source.forKey)}</span>
                                            <a className="shrink-0 text-[#7cc4ff] no-underline hover:underline" href={source.href} target="_blank" rel="noopener noreferrer">{source.name}</a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            {/* صفحات المدن الساكنة (‏/weather/). الرابط هنا ليس زينة: صفحة لا
                                يشير إليها رابطٌ من داخل الموقع تبقى «يتيمة» فيزحف إليها جوجل
                                ببطء ويُقلّل وزنها — وخريطة الموقع وحدها لا تعوّض عن ذلك. */}
                            <div className="mb-5">
                                <h3 className="text-sm font-bold m-0 mb-2 text-[var(--color-primary)]">{t('about.cityTitle')}</h3>
                                <a className="text-[13px] text-[#7cc4ff] no-underline hover:underline" href={locale.startsWith('ar') ? '/weather/' : '/weather/en/'}>{t('about.cityLink')}</a>
                            </div>
                            <div className="mb-5">
                                <h3 className="text-sm font-bold m-0 mb-2 text-[var(--color-primary)]">{t('about.mapTitle')}</h3>
                                <p className="m-0 text-[13px] leading-[1.7] opacity-85">{t('about.mapBody')}</p>
                            </div>
                            <div className="mb-5">
                                <h3 className="text-sm font-bold m-0 mb-2 text-[var(--color-primary)]">{t('about.updatedTitle')}</h3>
                                <p className="m-0 text-[13px] leading-[1.7] opacity-85">{new Date().toLocaleString(locale)}</p>
                            </div>
                            <div className="mb-5">
                                <h3 className="text-sm font-bold m-0 mb-2 text-[var(--color-primary)]">{t('about.followTitle')}</h3>
                                <div className="grid grid-cols-5 gap-2.5 pt-1 pb-[14px] px-0">
                                    {SOCIAL_LINKS.map(({ key, Icon, color, href }) => (
                                        <a
                                            key={key}
                                            className="group flex flex-col items-center gap-[7px] no-underline text-inherit cursor-pointer"
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <span className="w-12 h-12 flex items-center justify-center rounded-full text-white text-xl shadow-[0_4px_12px_rgba(0,0,0,0.25)] transition-transform duration-150 ease group-hover:-translate-y-[3px] group-hover:scale-[1.06]" style={{ background: color }}><Icon /></span>
                                            <span className="text-[11px] font-semibold opacity-85">{t(`social.${key}`)}</span>
                                        </a>
                                    ))}
                                </div>
                                <button
                                    className="flex items-center justify-center gap-2 w-full px-[14px] py-2.5 rounded-[10px] border border-[var(--border-color)] bg-transparent text-inherit text-[13px] font-bold cursor-pointer transition-[background] duration-150 ease hover:bg-white/[0.06]"
                                    type="button"
                                    onClick={() => { setShowInfo(false); setShowReport(true); }}
                                >
                                    <FiFlag /> {t('report.title')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                </ModalPortal>
            )}
            {showReport && (
                <ModalPortal>
                    <ReportProblemModal onClose={() => setShowReport(false)} />
                </ModalPortal>
            )}
            {showShare && (
                <ModalPortal>
                <div className="fixed inset-0 bg-black/70 backdrop-blur-[8px] flex items-center justify-center z-[2000] p-5 animate-fade-in" onClick={() => setShowShare(false)}>
                    <div className="w-full max-w-[420px] bg-[var(--panel-bg)] border border-[var(--panel-border)] text-inherit light:text-[var(--text-primary)] rounded-2xl overflow-hidden flex flex-col animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center px-[22px] py-[18px] border-b border-[var(--divider)]">
                            <h2 className="m-0 text-lg font-extrabold">{t('share.title')}</h2>
                            <button className="light:text-[var(--text-secondary)]" onClick={() => setShowShare(false)}>×</button>
                        </div>

                        <div className="grid grid-cols-5 gap-2.5 pt-5 px-[22px] pb-2">
                            {shareTargets.map(({ key, label, Icon, color, href }) => (
                                <a
                                    key={key}
                                    className="group flex flex-col items-center gap-[7px] no-underline text-inherit cursor-pointer"
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setShowShare(false)}
                                >
                                    <span className="w-12 h-12 flex items-center justify-center rounded-full text-white text-xl shadow-[0_4px_12px_rgba(0,0,0,0.25)] transition-transform duration-150 ease group-hover:-translate-y-[3px] group-hover:scale-[1.06]" style={{ background: color }}><Icon /></span>
                                    <span className="text-[11px] font-semibold opacity-85">{label}</span>
                                </a>
                            ))}
                        </div>

                        <div className="flex gap-2 pt-3 px-[22px] pb-[22px]">
                            <input className="flex-1 min-w-0 px-3 py-[9px] rounded-[10px] border border-[var(--border-color)] bg-[var(--hover-bg)] text-inherit text-xs [direction:ltr] text-left outline-none" type="text" value={shareUrl} readOnly onFocus={(e) => e.target.select()} />
                            <button className={`inline-flex items-center gap-1.5 px-[14px] py-[9px] rounded-[10px] border-none text-white text-xs font-bold cursor-pointer whitespace-nowrap transition-[background] duration-150 ease ${copied ? 'bg-[#1faa59]' : 'bg-[var(--color-primary)]'}`} onClick={handleCopyLink} aria-label={t('share.copyLink')}>
                                {copied ? <><FiCheck /> {t('share.copied')}</> : <><FiCopy /> {t('share.copy')}</>}
                            </button>
                        </div>
                    </div>
                </div>
                </ModalPortal>
            )}
        </>
    );
});
